import { Check } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { SOVEREIGNTY_POINTS, GOVERNANCE_DOMAINS } from "@/data/content";
import PageHero from "@/components/site/PageHero";
import WindTurbine from "@/components/site/WindTurbine";
import CTABand from "@/components/site/CTABand";

export default function Frameworks() {
  useDocumentTitle("Frameworks | Sovereignty and global standards — Meta61");
  return (
    <div>
      <PageHero
        title="Sovereignty at home."
        accent="Standards for the world."
        subhead="Meta61 works on both sides of the same coin — strengthening Australia's critical capability, and contributing to the global frameworks that govern trustworthy technology."
      />

      {/* Mandate 1 */}
      <section className="m61-section">
        <div className="m61-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><p className="m61-eyebrow mb-5">Mandate 1</p>
            <h2 className="m61-h2">Australian sovereignty &amp; critical standards</h2></div>
          <div className="lg:col-span-8">
            <p className="text-lg text-m61-muted leading-relaxed mb-8">
              Sovereignty is not isolation. It is the capacity to make and act on independent
              choices about the technologies a nation depends on. Meta61 helps strengthen Australia's
              national capability, critical-technology resilience, data and infrastructure
              sovereignty, and the trusted standards that underpin them — informed by Australia's AI
              Ethics Principles, the Voluntary AI Safety Standard, and the National AI Centre's
              Guidance for AI Adoption.
            </p>
            <ul className="space-y-3">
              {SOVEREIGNTY_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-m61-muted">
                  <Check className="w-5 h-5 text-m61-cyan shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Wind Turbine */}
      <section className="m61-section border-t border-m61-line bg-m61-surface/40">
        <div className="m61-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="m61-eyebrow mb-5">Our operating model</p>
            <h2 className="m61-h2 mb-5">The Wind Turbine — turning standards into sovereign capability.</h2>
            <p className="text-m61-muted leading-relaxed mb-6">
              Where KITE sets the <em>why</em>, the Wind Turbine model sets the <em>how</em> and
              <em> what</em>. It is the operating model Meta61 uses to convert standards, values, and
              partnerships into sovereign capability and trusted outcomes, with resilience and
              sustainability built in. Each component turns principle into power:
            </p>
            <ul className="space-y-2 text-sm text-m61-muted">
              <li><span className="text-m61-text font-medium">Rotor blades</span> — the values, policies, and critical standards (e.g., Australia's AI Ethics Principles, the Voluntary AI Safety Standard, ISO/IEC 42001) that drive the system ethically and efficiently.</li>
              <li><span className="text-m61-text font-medium">Gearbox</span> — the community, partners, and contributors who bring diversity, equity, and inclusion to the work.</li>
              <li><span className="text-m61-text font-medium">Generator</span> — the conversion into output: sovereign capability, trusted systems, and value for the nation and its institutions.</li>
              <li><span className="text-m61-text font-medium">Tail vane</span> — the operations and management that keep the system aligned and directed toward humanity and the SDGs.</li>
              <li><span className="text-m61-text font-medium">Distributed by design</span> — a preference for distributed, resilient architectures (e.g., fog/edge computing) that strengthen sovereignty and reduce the energy footprint of AI.</li>
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="max-w-md mx-auto"><WindTurbine /></div>
          </div>
        </div>
      </section>

      {/* Mandate 2 */}
      <section className="m61-section">
        <div className="m61-container">
          <p className="m61-eyebrow mb-5">Mandate 2</p>
          <h2 className="m61-h2 mb-4 max-w-3xl">Global standards, policy &amp; sustainability</h2>
          <p className="text-lg text-m61-muted leading-relaxed max-w-3xl mb-12">
            The frameworks that will govern AI and emerging technology for a generation are being
            written now, in the world's peak bodies. Meta61 contributes to that work — and helps
            Australia shape it. Our domains:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-m61-line border border-m61-line">
            {GOVERNANCE_DOMAINS.map((d) => (
              <div key={d.title} className="bg-m61-surface p-8">
                <h3 className="m61-h3 mb-3">{d.title}</h3>
                <p className="text-m61-muted text-sm leading-relaxed">{d.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* From principle to practice */}
      <section className="m61-section border-t border-m61-line">
        <div className="m61-container max-w-3xl">
          <p className="m61-eyebrow mb-5">From principle to practice</p>
          <p className="text-2xl lg:text-3xl font-display text-m61-text leading-snug">
            Principles are necessary but not sufficient. Meta61's value is in operationalising them —
            translating high-level frameworks into the architectures, controls, and assurance that
            make a system <span className="m61-gradient-text">trustworthy in production.</span>
          </p>
        </div>
      </section>

      <CTABand showAssessment />
    </div>
  );
}
